import { useEffect, useState } from "react";
import Header from "../component/menu/Header";
import { constants, sortingOptions1 } from "../constant/constants";
import SideMenu from "../component/menu/SideMenu";
import { ConfigProvider, Pagination } from "antd";
import OrderComponent from "../component/OrderComponent";
import { showCookingTimeField } from "../utils/Common";
import ConfirmationModal from "../component/modals/ConfirmationModal";
import DisplayInvoice from "../component/modals/DisplayInvoiceModal";
import useUserStore from "../stores/store";
import ApiService from "../utils/ApiService";
import { getFormatedOrderList } from "../services/order-details-service";
import OrderPaymentModal from "../component/modals/OrderPaymentModal";
import { Spin } from "antd";
import DataNotFound from "../component/common/DataNotFound";
import { formatOrderFilter, getPaymentStatusValue } from "../services/order-processing-service";
import TableHeaderComponent from "../component/TableHeaderComponent";
import CustomLoader from "../component/menu/CustomLoader";
import useOrderStore from "../stores/useOrderStore";
import { getRestaurantIdWithAuthToken } from "../utils/authUtils";
import { getOrderDefaultFilter } from "../utils/store.helper";
import OrderPaymentRefundModal from "../component/modals/OrderPaymentRefundModal";

function Order() {
  const { user, tableManagement, showNotification } = useUserStore();
  const {
    newOrderId,
    hasFetchedOrders,
    orderRecordsFilter,
    orderRecords,
    setOrderRecords,
    orderRecordsTotal,
    setOrderRecordsTotal,
    shouldFetchOrderRecords,
    setShouldFetchOrderRecords,
    setOrderRecordsFilter,
    setHasFetchedOrders,
    setNewOrderId,
  } = useOrderStore();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDisplayInvoice, setIsDisplayInvoice] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPaymentRefundModalOpen, setIsPaymentRefundModalOpen] = useState(false);
  const [amountToPay, setAmountToPay] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [showCookingTime, setShowCookingTime] = useState(false);
  const [cookingTime, setCookingTime] = useState("30");
  const [titleMessage, setTitleMessage] = useState("");
  const [action, setAction] = useState("");
  const [selectedItem, setSelectedItem] = useState({});
  const [searchText, setSearchText] = useState("");
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [shouldLockRow, setShouldLockRow] = useState("");

  const onHandleOrderAction = (val, action) => {
    setShowCookingTime(showCookingTimeField(val));
    setTitleMessage(`<span class="text-purple">Order ID</span> - ${val.order_id}`);
    switch (action) {
      case constants.ORDER.INVOICE: {
        setSelectedItem(val);
        setAction(constants.ORDER.INVOICE);
        setIsDisplayInvoice(true);
        break;
      }
      case constants.ORDER.ACCEPT: {
        if (val.paid === 0) {
          // If unpaid, open the payment modal
          setAction(constants.ORDER.WAITING);
          setAmountToPay(val.total);
          setSelectedItem(val);
          setIsPaymentModalOpen(true);
          setShowCookingTime(showCookingTimeField({ status: "waiting" }));
        } else if (val.paid === 1) {
          // If paid, proceed with marking the order as "Waiting"
          setConfirmationMessage(`Are you sure you want to mark the order as processing for <b>0${val.name}</b> user?`);
          setSelectedItem(val);
          setAction(constants.ORDER.WAITING);
          setIsPaymentModalOpen(true);
          setShowCookingTime(showCookingTimeField({ status: "waiting" }));
        }
        break;
      }
      case constants.ORDER.PAID: {
        setAmountToPay(val.total);
        setSelectedItem(val);
        setIsPaymentModalOpen(true);
        setShowCookingTime(false);
        break;
      }
      case constants.ORDER.REFUND: {
        setSelectedItem(val);
        setIsPaymentRefundModalOpen(true);
        break;
      }
      default:
        break;
    }
  };
  const onConfirm = async (record, action) => {
    setIsConfirmModalOpen(false);
    if (action === constants.ORDER.PAID) {
      record._id = record.orderId;
    }

    statusChange(record);
  };

  const statusChange = async (record) => {
    setShouldLockRow(record._id);

    let declined = await ApiService.ORDER_STATUS_CHANGE(
      {
        orderId: record._id,
        selection: 1,
        cookingTime: record.cookingTime || "",
        payAfterEat: record.payAfterEat ? "YES" : "NO",
      },
      user.token.token
    );

    if (declined.code !== 200) {
      setShouldLockRow("");
      showNotification(declined?.data?.message || "Something went wrong. Please try again later.");
      return;
    }

    setShouldLockRow("");
    setIsPaymentModalOpen(false);
  };

  const onNotConfirm = () => {
    setIsPaymentModalOpen(false);
    setIsConfirmModalOpen(false);
  };

  const onClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
  };

  const onFinishPaymentModal = async (result) => {
    if (result.success) {
      setIsPaymentModalOpen(false);
    }
  };

  const onSearchText = (value) => {
    const newFilter = formatOrderFilter(orderRecordsFilter, "SEARCH", value.trim());
    setOrderRecordsFilter(newFilter);
    fetchOrderList(newFilter);
  };

  const onSortRecord = (value) => {
    const newFilter = formatOrderFilter(orderRecordsFilter, "SORTING", value);
    setOrderRecordsFilter(newFilter);
    fetchOrderList(newFilter);
  };

  const onPaymentStatusChange = (value) => {
    const newFilter = formatOrderFilter(orderRecordsFilter, "PAYMENT_STATUS", value);
    setOrderRecordsFilter(newFilter);
    fetchOrderList(newFilter);
  };

  const onOrderStatusChange = (value) => {
    const newFilter = formatOrderFilter(orderRecordsFilter, "ORDER_STATUS", value);
    setOrderRecordsFilter(newFilter);
    fetchOrderList(newFilter);
  };

  const fetchOrderList = async (params) => {
    try {
      // It will ensure that token and restaurant id will be present if exist in localStorage and remove the dependency from restaurantId and user state which may appear later from state
      const auth = getRestaurantIdWithAuthToken();
      params.restaurantId = auth.restaurantId;

      setIsOrderLoading(true);

      const res = await ApiService.FILTER_ORDER_LIST(params, auth.token);

      if (!res || (res && res.code !== 200)) {
        showNotification(res?.data?.message || "Something went wrong. Please try again later.");
        return;
      }

      let formatedOrders = res.payload.records.map((o) => {
        return getFormatedOrderList(o, orderRecordsFilter.page);
      });

      setOrderRecords(formatedOrders);
      setOrderRecordsTotal(res.payload.total);
    } catch (error) {
      showNotification(error.response.data.message);
    } finally {
      setIsOrderLoading(false);
      setHasFetchedOrders(true);
    }
  };

  const onPageChange = (page) => {
    const newFilter = { ...orderRecordsFilter, page };
    setOrderRecordsFilter(newFilter);
    fetchOrderList(newFilter);
  };

  const onShowSizeChange = (current, pageSize) => {
    // It is not actually used as there is no page size change option in UI
  };

  useEffect(() => {
    if (shouldFetchOrderRecords) {
      fetchOrderList(getOrderDefaultFilter());
      setShouldFetchOrderRecords(false);
    }
  }, [shouldFetchOrderRecords]);

  useEffect(() => {
    setSearchText(orderRecordsFilter?.searchText || '')
  }, [orderRecordsFilter]);

  useEffect(() => {
    if (!newOrderId) {
      return;
    }

    // Stop the blinking animation after 3 seconds
    const timer = setTimeout(() => {
      setNewOrderId("");
    }, 3000);

    // Cleanup the timeout
    return () => clearTimeout(timer);
  }, [newOrderId]);

  const handleClosingOfDisplayInvoiceModal = () => {
    setIsDisplayInvoice(false);
  };

  return (
    <div className="flex flex-col">
      <CustomLoader />
      <Header title={`McDonalds Food Chain`} menu={constants.MENU.ORDER} currentMenuKey={"T_4_S_4"} />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.ORDER} />
        <div className="flex flex-row p-[24px] w-full mt-[80px] ml-[72px]">
          <div className="flex flex-col w-full">
            <TableHeaderComponent
              pageRoute={tableManagement === 'Active' ? '/table':'/order/add'}
              searchText={searchText}
              setSearchText={setSearchText}
              sortingOptions={sortingOptions1}
              onChangeSorting={onSortRecord}
              record={{
                title: "All Orders",
                hint: "Search in Orders",
                paymentStatusValue: getPaymentStatusValue(orderRecordsFilter),
                orderStatusValue: orderRecordsFilter?.orderStatus || undefined, // not empty but either value or undefined for ant-design
                search: onSearchText,
                paymentStatus: onPaymentStatusChange,
                orderStatus: onOrderStatusChange,
              }}
            />

            <div className="flex flex-col">
              {isOrderLoading ? (
                <div className="h-[70vh] flex items-center justify-center">
                  <Spin size="large" />
                </div>
              ) : (
                orderRecords?.map((item, index) => (
                  <div key={`order${index}`}>
                    <OrderComponent
                      record={item}
                      callback={onHandleOrderAction}
                      newOrderId={newOrderId}
                      shouldLockRow={shouldLockRow}
                      isTableManagement={tableManagement === 'Active'}
                    />
                  </div>
                ))
              )}

              {hasFetchedOrders && !isOrderLoading && orderRecords.length === 0 && <DataNotFound />}
            </div>
            <div className="flex flex-row justify-start font-uregular">
              <ConfigProvider
                theme={{
                  components: {
                    Pagination: {
                      itemActiveBg: "#FFD700",
                      fontFamily: "ubold",
                      colorPrimaryBorder: "#FFD700",
                      colorPrimary: "#111111",
                      colorPrimaryHover: "#111111",
                      itemInputBg: "#111111",
                      itemBg: "#E5E4E9",
                    },
                  },
                }}
              >
                <Pagination
                  total={orderRecordsTotal}
                  showSizeChanger={false}
                  onShowSizeChange={onShowSizeChange}
                  showTotal={(total) => `Total ${total} items`}
                  defaultPageSize={orderRecordsFilter.perPage}
                  defaultCurrent={orderRecordsFilter.page}
                  current={orderRecordsFilter.page}
                  onChange={onPageChange}
                />
              </ConfigProvider>
            </div>
          </div>
          <ConfirmationModal
            openModal={isConfirmModalOpen}
            records={{
              onFinish: onConfirm,
              onCancel: onNotConfirm,
              titleMessage,
              showCookingTime,
              cookingTime: "30",
              action: action,
              message: confirmationMessage,
              param: selectedItem,
            }}
          />

          <OrderPaymentModal
            totalAmount={amountToPay}
            cookingTime={cookingTime}
            openModal={isPaymentModalOpen}
            params={{
              onFinish: onFinishPaymentModal,
              onCancel: selectedItem.paid === 0 ? onNotConfirm : onClosePaymentModal,
              // order: selectedItem,
              order: {...selectedItem, uuid: crypto.randomUUID()},
            }}
          />
          <OrderPaymentRefundModal 
            openModal = {isPaymentRefundModalOpen}
            centered
            params = {{
              onCancel: () => setIsPaymentRefundModalOpen(false),
              onHandleOk: () => setIsPaymentRefundModalOpen(false),
              order: selectedItem?.total ? {...selectedItem} : {total: 0}
            }}
          />

          <DisplayInvoice
            openModal={isDisplayInvoice}
            records={{
              onCancel: handleClosingOfDisplayInvoiceModal,
              param: selectedItem,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default Order;
